import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LOGO_URL } from '../../constants';
import { Instagram } from 'lucide-react';
import { trackPixelEvent } from '../utils/pixel';
import SecurityBadges from './SecurityBadges';

import { useAffiliate } from '../hooks/useAffiliate';

import { StoreConfig, db, MenuItem, Page, AdminCategory } from '../services/backend';

interface FooterProps {
  config?: StoreConfig['banners']['footer'];
  categories?: AdminCategory[];
}

const Footer: React.FC<FooterProps> = ({ config: bannerConfig, categories = [] }) => {
  const navigate = useNavigate();
  const affiliate = useAffiliate();
  const [institutionalLinks, setInstitutionalLinks] = useState<{ label: string; url: string }[]>(
    []
  );
  const [storeConfig, setStoreConfig] = useState<StoreConfig | null>(null);

  useEffect(() => {
    const loadData = async () => {
      // Load Config
      const config = await db.getStoreConfig();
      setStoreConfig(config);

      // Load Menus
      const menus = await db.getMenus();
      const footerMenus = menus
        .filter((m) => m.type === 'footer' || m.type === 'both')
        .sort((a, b) => a.order - b.order);

      // Load Pages
      const pages = await db.getPages();
      const footerPages = pages.filter((p) => p.location === 'footer');

      // Combine
      const links = [
        ...footerMenus.map((m) => ({ label: m.label, url: m.url })),
        ...footerPages.map((p) => ({ label: p.title, url: `/paginas/${p.slug}` })),
      ];
      setInstitutionalLinks(links);
    };
    loadData();
  }, []);

  const handleNavClick = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleContactClick = (type: 'whatsapp' | 'email' | 'phone') => {
    trackPixelEvent('Contact', { type });
  };

  return (
    <footer className="bg-alphamed-dark text-alphamed-cream pt-16 pb-8 border-t border-alphamed-gold/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="flex flex-col items-start">
            <img
              src={LOGO_URL}
              alt="Alpha Estética Logo"
              className="w-[150px] h-auto mb-6 brightness-0 invert opacity-90"
            />
            <p className="text-sm text-white/60 leading-relaxed mb-8">
              Tecnologia e qualidade premium para clínicas e profissionais de estética.
              Transformando ciência em beleza.
            </p>

            {/* Ambassador Mini-Bio */}
            <div
              className="flex items-center gap-4 bg-white/5 p-3 rounded-lg border border-white/10 w-full cursor-pointer hover:bg-white/10 transition-colors"
              onClick={() => navigate('/embaixadora')}
            >
              <div className="w-10 h-10 rounded-full overflow-hidden border border-alphamed-gold flex-shrink-0">
                <img
                  src="https://res.cloudinary.com/dwkqdnp4h/image/upload/v1764596680/mbsodgl9tjr44pmg1xtm.webp"
                  alt="Dra. Ariana"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-xs font-bold text-alphamed-gold">Dra. Ariana Marinari</p>
                <p className="text-[10px] text-white/60">Embaixadora Alpha Estética • Saiba mais</p>
              </div>
            </div>
          </div>

          {/* Institucional */}
          <div>
            <h4 className="font-serif text-lg text-alphamed-gold mb-6">Institucional</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li>
                <button
                  onClick={() => handleNavClick('/blog')}
                  className="hover:text-white transition-colors text-left"
                >
                  Blog
                </button>
              </li>
              {institutionalLinks.map((link, idx) => (
                <li key={idx}>
                  <button
                    onClick={() => handleNavClick(link.url)}
                    className="hover:text-white transition-colors text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Catálogo */}
          <div>
            <h4 className="font-serif text-lg text-alphamed-gold mb-6">Catálogo</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li>
                <button
                  onClick={() => handleNavClick('/produtos')}
                  className="hover:text-white transition-colors"
                >
                  Ver Todos
                </button>
              </li>
              {categories.map((cat) => (
                <li key={cat.id}>
                  <button
                    onClick={() => handleNavClick(`/produtos/${cat.slug}`)}
                    className="hover:text-white transition-colors text-left"
                  >
                    {cat.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg text-alphamed-gold mb-6">Contato</h4>
            <ul className="space-y-3 text-sm text-white/70 mb-6">
              <li>
                <span className="text-white/40 block text-xs mb-1">Suporte</span>
                <a
                  href={`https://wa.me/${storeConfig?.contacts?.whatsapp ? storeConfig.contacts.whatsapp : '5562992213411'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleContactClick('whatsapp')}
                  className="hover:text-white transition-colors"
                >
                  {storeConfig?.contacts?.whatsapp
                    ? `+${storeConfig.contacts.whatsapp.slice(0, 2)} (${storeConfig.contacts.whatsapp.slice(2, 4)}) ${storeConfig.contacts.whatsapp.slice(4)}`
                    : '62 99221-3411'}
                </a>
              </li>
              <li>
                <span className="text-white/40 block text-xs mb-1">Contato</span>
                <a
                  href={`tel:${storeConfig?.contacts?.phone ? storeConfig.contacts.phone.replace(/\D/g, '') : '556232776347'}`}
                  onClick={() => handleContactClick('phone')}
                  className="hover:text-white transition-colors"
                >
                  {storeConfig?.contacts?.phone || '062 3277-6347'}
                </a>
              </li>
              <li>
                <span className="text-white/40 block text-xs mb-1">Email</span>
                <a
                  href={`mailto:${storeConfig?.contacts?.email || 'alphamedestetica@gmail.com'}`}
                  onClick={() => handleContactClick('email')}
                  className="hover:text-white transition-colors"
                >
                  {storeConfig?.contacts?.email || 'alphamedestetica@gmail.com'}
                </a>
              </li>

              <li className="pt-2 leading-relaxed">
                <span className="text-white/40 block text-xs mb-1">Endereço</span>
                <span className="whitespace-pre-line">
                  {storeConfig?.contacts?.address ||
                    'Rua J 46, Qd 46, Lt 20, Galpão 001, Sala 001\nMansões Paraíso, Aparecida de Goiânia - GO\nCEP: 74952-335'}
                </span>
              </li>
            </ul>

            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/alphaestetica.com.br/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-alphamed-gold hover:border-alphamed-gold transition-all text-white group"
                title="Siga-nos no Instagram"
              >
                <Instagram size={20} className="group-hover:scale-110 transition-transform" />
              </a>
            </div>
          </div>
        </div>

        <SecurityBadges />

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-white/40">
          <p className="text-center md:text-left leading-relaxed">
            &copy; ESTÉTICA DISTRIBUIDORA E IMPORTADORA LTDA - 64.188.921/0001-02 - 2025.
            <br className="md:hidden" /> Todos os direitos reservados.
          </p>

          {affiliate && (
            <div className="mt-4 md:mt-0 px-4 py-1 border border-alphamed-gold/30 rounded-full bg-alphamed-gold/10 text-alphamed-gold text-xs font-medium flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              Apoiando: {affiliate.name || affiliate.affiliateCode}
            </div>
          )}

          <p className="text-center md:text-right mt-2 md:mt-0">v0.0.2</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
